import * as React from 'react';
import { ApplicationState, AppThunkDispatch } from "../../store";
import { connect } from "react-redux";
import EntityList from "../../components/EntityList";
import { EntityListStateProps, EntityListDispatchProps, EntityItemTab } from "../../components/EntityList/EntityList";
import { EntityType, Entity, ProfessionalEntity, ClientEntity, UserEntity } from "../../store/entities/state";
import { loadEntities, entityCreating } from "../../store/entities/actions";
import { getProfessionalsAsync, getUsersAsync } from "../../ajax/requests";
import { getEntitiesData, getEntitiesDataItems } from "../../store/entities/selectors";
import { TK } from "../../store/translations/translationKeys";
import { FeatureEnum } from '../../models';
import { canEditProfessionals, hasFeature } from '../../store/session/selectors';
import ProfessionalEditForm from './ProfessionalEditForm';
import ProfessionalAppointmentsTable from './ProfessionalAppointmentsTable';
import ProfessionalItemContent from './ProfessionalItemContent/ProfessionalItemContent';
import ClientPacksTable from '../Clients/ClientPacksTable';
import ProfessionalWaitingList from './ProfessionalWaitingList';
import ProfessionalTasksTable from './ProfessionalTasksTable';
import ProfessionalFeesTable from './ProfessionalFeesTable';
import { translate } from '../../store/translations/hooks';

const getTabs = (state: ApplicationState): EntityItemTab[] => {
    const tabs: EntityItemTab[] = [
        { id: 'data', title: translate(TK.data), render: (_, entity, onClose) => (<ProfessionalEditForm data={{...entity} as ProfessionalEntity} onClose={onClose}/>) },
        { id: 'appointments', title: translate(TK.appointments), render: (_, entity) => (<ProfessionalAppointmentsTable professional={entity as ProfessionalEntity} />) },
        { id: 'waitingList', title: translate(TK.waitingList), render: (isOpen, entity) => (<ProfessionalWaitingList isOpen={isOpen} professional={entity as ProfessionalEntity} />) },
    ]

    if (state.settings.taskManagement?.enabled) {
        tabs.push({ id: 'tasks', title: translate(TK.tasks), render: (isOpen, entity) => (<ProfessionalTasksTable isOpen={isOpen} professional={entity as ProfessionalEntity} />) })
    }

    if (hasFeature(FeatureEnum.ProfessionalPacks)(state)) {
        tabs.push({
            id: 'packs',
            title: translate(TK.packs),
            render: (isOpen, entity) => (<ClientPacksTable isOpen={isOpen} client={entity as ClientEntity} clientIsProfessional />)
        })
    }

    tabs.push({
        id: 'honorarios',
        title: translate(TK.honorarios),
        render: (isOpen, entity) => (<ProfessionalFeesTable isOpen={isOpen} professional={entity as ProfessionalEntity} />),
    })

    return tabs
}

const mapStateToProps = (state: ApplicationState): EntityListStateProps => ({
    id: 'professionals',
    entityType: 'professionals',
    title: translate(TK.professionals),
    entitiesData: getEntitiesData<ProfessionalEntity>('professionals')(state),
    searchOptions: getEntitiesDataItems<ProfessionalEntity>('professionals')(state).map(e => e.name),
    renderItemHeader: (entity: Entity) => (<ProfessionalItemContent professional={entity as ProfessionalEntity} />),
    itemTabs: getTabs(state),
    canCreateEntities: [
        canEditProfessionals(state) && 'professionals',
    ].filter(x => x) as EntityType[],
});

const mapDispatchToProps = (dispatch: AppThunkDispatch): EntityListDispatchProps => ({
    onLoad: (keyword?: string, offset?: number) => {
        // Load professionals
        dispatch(loadEntities<ProfessionalEntity>('professionals', (pageSize: number) => getProfessionalsAsync(keyword, pageSize), offset ? 'AppendResult' : 'NewResult', () => !offset));
        // Also load users if this is the initial load (not pagination)
        if (!offset) {
            dispatch(loadEntities<UserEntity>('users', (pageSize: number) => getUsersAsync(undefined, pageSize), 'NewResult'));
        }
    },
    onCreateEntity: (entityType: EntityType) => dispatch(entityCreating(entityType)),
});

export default connect<EntityListStateProps, EntityListDispatchProps, void, ApplicationState>(
    mapStateToProps,
    mapDispatchToProps,
)(EntityList as any);
